// src/components/DetalleZonificacionIndustrial.js
//
// Modal de detalle para Zonificación Industrial (sublayer 3 de parques).
// Reutiliza los mismos estilos .pdet-* y .parq-popup-* del modal de parques
// para mantener look consistente.

import React, { useEffect, useState } from "react";
import parquesIndustriales from "../services/parquesService";
import CloseIcon from "@mui/icons-material/CloseRounded";
import "./DetalleParqueIndustrial.css";
import "./popup-styles.css";

// IDs de infraestructura — copiados de loadParqueZonificacionIndustriaDetalle
// en ArcGISFeatureLayer.js (línea 2207). Cualquier cambio ahí debe replicarse.
const INFRA_IDS = {
  MZ: 11,
  ETAPA: 14,
  AREA: 21,
  ZONIFICACION: 29,
  LOTES_DISPONIBLES: 53,
  TOTAL_LOTES: 55,
  VENDIDOS: 58,
  RESERVADOS: 59,
};

const ZONIFICACION_LABELS = {
  1: "I1",
  2: "I2",
  3: "I3",
  4: "I4",
};

const capitalizeWords = (str = "") =>
  String(str)
    .toLowerCase()
    .replace(/(^|\s)\S/g, (t) => t.toUpperCase());

const KV = ({ label, value }) => {
  if (!value || value === "-" || String(value).trim() === "") return null;
  return (
    <div className="parq-popup-kv">
      <span className="parq-popup-kv-label">{label}</span>
      <span className="parq-popup-kv-value">{value}</span>
    </div>
  );
};

const Section = ({ title, children }) => {
  const hasContent = React.Children.toArray(children).some((c) => c);
  if (!hasContent) return null;
  return (
    <div className="parq-popup-section">
      <h5 className="parq-popup-section-title">{title}</h5>
      <div className="parq-popup-grid">{children}</div>
    </div>
  );
};

const DetalleZonificacionIndustrial = ({
  idIntervencion,
  idZonificacionInfra,
  idZonificacion,
  nombre,
  onClose,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!idIntervencion || !idZonificacionInfra) return;
    let cancelled = false;
    setLoading(true);
    setData(null);

    // Usa el mismo endpoint que loadParqueZonificacionIndustriaDetalle.
    parquesIndustriales
      .getIntervencionParamDetalle(idIntervencion, idZonificacionInfra, 3)
      .then((response) => {
        if (cancelled) return;
        // Soportar distintas formas de respuesta — igual que el popup viejo.
        let row = null;
        if (Array.isArray(response)) row = response[0] || null;
        else if (Array.isArray(response?.items)) row = response.items[0] || null;
        else if (response?.item) row = response.item;
        else row = response || null;
        const lista = Array.isArray(row?.lista_param) ? row.lista_param : [];
        const getValue = (idInfra) => {
          const r = lista.find((x) => x.id_infraestructura === idInfra);
          return (r?.valor_descripcion || "").trim();
        };
        setData({
          etapa: getValue(INFRA_IDS.ETAPA),
          mz: getValue(INFRA_IDS.MZ),
          area: getValue(INFRA_IDS.AREA),
          totalLotes: getValue(INFRA_IDS.TOTAL_LOTES),
          vendidos: getValue(INFRA_IDS.VENDIDOS),
          reservados: getValue(INFRA_IDS.RESERVADOS),
          disponibles: getValue(INFRA_IDS.LOTES_DISPONIBLES),
          zonificacion:
            getValue(INFRA_IDS.ZONIFICACION) ||
            ZONIFICACION_LABELS[idZonificacion] ||
            "-",
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [idIntervencion, idZonificacionInfra, idZonificacion]);

  return (
    <div className="pdet-card">
      <button
        type="button"
        className="pdet-close"
        onClick={onClose}
        aria-label="Cerrar"
      >
        <CloseIcon style={{ fontSize: 20 }} />
      </button>

      <div className="pdet-card-head">
        <span className="pdet-pill">Zonificación Industrial</span>
        <h2 className="pdet-title">
          {data?.mz && data.mz !== "-"
            ? `Mz. ${capitalizeWords(data.mz)}`
            : nombre || "Zonificación"}
        </h2>
      </div>

      <div className="pdet-card-body">
        {loading ? (
          <div className="pdet-loading">Cargando información…</div>
        ) : (
          data && (
            <>
              <Section title="General">
                <KV label="Etapa" value={data.etapa} />
                <KV label="Manzana" value={data.mz} />
                <KV label="Área" value={data.area} />
                <KV label="Zonificación" value={data.zonificacion} />
              </Section>

              <Section title="Lotes">
                <KV label="Total de lotes" value={data.totalLotes} />
                <KV label="Lotes vendidos" value={data.vendidos} />
                <KV label="Lotes reservados" value={data.reservados} />
                <KV label="Lotes disponibles" value={data.disponibles} />
              </Section>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default DetalleZonificacionIndustrial;
